import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@material-ui/core'

import { Lead } from '../../../models/Outreach/types'
import { useAccountInvite } from '../../../hooks/Outreach/useAccountInvite'

function prospectEmail(lead: Lead | null): string {
    if (!lead) {
        return ''
    }
    const nested = lead.campaign_prospect?.prospect?.email
    const nestedValue =
        typeof nested === 'string' ? nested : nested?.value ?? ''
    return (
        lead.identity?.email || lead.prospect_detail?.email || nestedValue || ''
    )
}

interface InviteToApplyDialogProps {
    open: boolean
    lead: Lead | null
    onClose: () => void
    onSuccess?: () => void
}

export const InviteToApplyDialog: React.FC<InviteToApplyDialogProps> = ({
    open,
    lead,
    onClose,
    onSuccess,
}) => {
    const {
        inviteToApply,
        resendAccountInvite,
        isInviting,
        isResending,
    } = useAccountInvite()
    const [email, setEmail] = useState(() => prospectEmail(lead))

    useEffect(() => {
        if (open) {
            setEmail(prospectEmail(lead))
        }
    }, [open, lead])

    if (!lead) {
        return null
    }

    const isResend = lead.account_status === 'invited'
    const isActive = lead.account_status === 'active'
    const loading = isInviting || isResending

    const handleSubmit = async () => {
        if (isActive) {
            onClose()
            return
        }

        if (isResend) {
            await resendAccountInvite({
                leadId: lead.id,
                email: email.trim() || undefined,
            })
        } else {
            await inviteToApply({
                leadId: lead.id,
                email: email.trim() || undefined,
            })
        }
        onSuccess?.()
        onClose()
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isActive
                    ? 'Account active'
                    : isResend
                    ? 'Resend account invite'
                    : 'Invite to apply'}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" paragraph>
                    {isActive
                        ? 'This lead already has an active tenant portal account.'
                        : 'Send an email inviting the prospect to create a tenant portal account and begin their application.'}
                </Typography>
                {!isActive && (
                    <TextField
                        fullWidth
                        label="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        margin="normal"
                        helperText="Updating the email here also updates the prospect's primary email."
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                {!isActive && (
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading || !email.trim()}
                    >
                        {loading
                            ? 'Sending…'
                            : isResend
                            ? 'Resend invite'
                            : 'Send invite'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

function AccountStatusChip({
    status,
}: {
    status?: 'none' | 'invited' | 'active' | null
}) {
    const colors = {
        none: { bg: '#f3f4f6', fg: '#6b7280', label: 'No account' },
        invited: { bg: '#dbeafe', fg: '#1d4ed8', label: 'Invited' },
        active: { bg: '#d1fae5', fg: '#047857', label: 'Active' },
    }
    const key = status ?? 'none'
    const { bg, fg, label } = colors[key]
    return (
        <Box
            component="span"
            style={{
                backgroundColor: bg,
                color: fg,
                fontWeight: 600,
                fontSize: '0.75rem',
                padding: '2px 8px',
                borderRadius: 999,
            }}
        >
            {label}
        </Box>
    )
}

export { AccountStatusChip }
